import { BookOpen, Globe, Info, RefreshCw, Repeat2, Timer } from 'lucide-react';
import { formatNumber } from '@/lib/utils';
import { formatDurationSec } from '@/hooks/useProductSessionAnalytics';
import {
    ATLAS_TYPE_LABELS,
    CHANNEL_LABELS,
    SURFACE_LABELS,
    useProductArticleAnalytics,
    type ArticleTypeRow,
} from '@/hooks/useProductArticleAnalytics';

function fmtPct(v: number | null): string {
    if (v == null) return 'n/d';
    return `${v.toLocaleString('es-ES', { maximumFractionDigits: 1 })}%`;
}

function typeLabel(row: ArticleTypeRow): string {
    if (row.surface === 'atlas') return ATLAS_TYPE_LABELS[row.key] ?? row.label;
    return row.label || row.key;
}

interface ProductArticleBehaviorPanelProps {
    days: number;
}

export function ProductArticleBehaviorPanel({ days }: ProductArticleBehaviorPanelProps) {
    const { data, isLoading, isError, error, refetch, isFetching } = useProductArticleAnalytics(days);

    if (isLoading && !data) {
        return (
            <div className="rounded-xl border border-border bg-card p-8 flex flex-col items-center gap-2">
                <RefreshCw size={20} className="animate-spin text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Calculando lectura de artículos…</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-5 space-y-2">
                <p className="text-sm font-semibold text-rose-400">No se pudo cargar el comportamiento de artículos</p>
                <p className="text-xs text-muted-foreground">
                    {(error as Error)?.message || 'Error al consultar get_product_article_analytics'}
                </p>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-primary-foreground"
                >
                    <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                    Reintentar
                </button>
            </div>
        );
    }

    if (!data) return null;
    const o = data.overview;
    const blogTypes = data.by_type.filter((t) => t.surface === 'blog');
    const atlasTypes = data.by_type.filter((t) => t.surface === 'atlas');

    return (
        <section className="space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h3 className="text-sm font-semibold text-foreground">Artículos: blog y Atlas</h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                        App y web · tipos, tiempo de lectura y salto entre piezas
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Recargar artículos"
                >
                    <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-xl border border-border bg-card p-4">
                    <p className="text-xs text-muted-foreground">Leen blog</p>
                    <p className="text-xl font-bold text-foreground mt-1 tabular-nums">
                        {formatNumber(o.blog_readers)}
                        <span className="text-sm font-semibold text-muted-foreground ml-1">({fmtPct(o.blog_pct)})</span>
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{formatNumber(o.blog_reads)} lecturas</p>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                    <p className="text-xs text-muted-foreground">Leen Atlas</p>
                    <p className="text-xl font-bold text-foreground mt-1 tabular-nums">
                        {formatNumber(o.atlas_readers)}
                        <span className="text-sm font-semibold text-muted-foreground ml-1">({fmtPct(o.atlas_pct)})</span>
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{formatNumber(o.atlas_reads)} fichas</p>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Timer size={12} /> Tiempo de lectura</p>
                    <p className="text-xl font-bold text-foreground mt-1 tabular-nums">{formatDurationSec(o.dwell_p50_sec)}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">p50 · media {formatDurationSec(o.dwell_avg_sec)}</p>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Repeat2 size={12} /> Saltan de artículo</p>
                    <p className="text-xl font-bold text-foreground mt-1 tabular-nums">
                        {formatNumber(o.hop_readers)}
                        <span className="text-sm font-semibold text-muted-foreground ml-1">({fmtPct(o.hop_pct)})</span>
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                        {o.avg_articles_per_reader ?? 'n/d'} piezas distintas por lector
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="rounded-xl border border-border bg-card p-4">
                    <p className="text-sm font-semibold text-foreground mb-3">App vs web</p>
                    {data.by_channel.length === 0 ? (
                        <p className="text-xs text-muted-foreground">Aún no hay desglose de canal</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[11px] text-muted-foreground text-left">
                                    <th className="pb-2 font-medium">Canal</th>
                                    <th className="pb-2 font-medium text-right">Lectores</th>
                                    <th className="pb-2 font-medium text-right">Lecturas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.by_channel.map((row) => (
                                    <tr key={row.key} className="border-t border-border/60">
                                        <td className="py-1.5">{CHANNEL_LABELS[row.key] ?? row.key}</td>
                                        <td className="py-1.5 text-right tabular-nums">{formatNumber(row.readers)}</td>
                                        <td className="py-1.5 text-right tabular-nums">{formatNumber(row.reads)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                    <p className="text-sm font-semibold text-foreground mb-3">Blog vs Atlas</p>
                    {data.by_surface.length === 0 ? (
                        <p className="text-xs text-muted-foreground">Sin lecturas en el periodo</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[11px] text-muted-foreground text-left">
                                    <th className="pb-2 font-medium">Superficie</th>
                                    <th className="pb-2 font-medium text-right">Lectores</th>
                                    <th className="pb-2 font-medium text-right">Tiempo p50</th>
                                    <th className="pb-2 font-medium text-right">Saltan</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.by_surface.map((row) => (
                                    <tr key={row.key} className="border-t border-border/60">
                                        <td className="py-1.5">{SURFACE_LABELS[row.key] ?? row.key}</td>
                                        <td className="py-1.5 text-right tabular-nums">{formatNumber(row.readers)}</td>
                                        <td className="py-1.5 text-right tabular-nums">{formatDurationSec(row.dwell_p50_sec)}</td>
                                        <td className="py-1.5 text-right tabular-nums">{fmtPct(row.hop_pct)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="rounded-xl border border-border bg-card p-4">
                    <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                        <BookOpen size={14} /> Tipos de blog
                    </p>
                    {blogTypes.length === 0 ? (
                        <p className="text-xs text-muted-foreground">Sin tipologías en este periodo</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[11px] text-muted-foreground text-left">
                                    <th className="pb-2 font-medium">Tipo</th>
                                    <th className="pb-2 font-medium text-right">Lectores</th>
                                    <th className="pb-2 font-medium text-right">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {blogTypes.map((row) => (
                                    <tr key={row.key} className="border-t border-border/60">
                                        <td className="py-1.5">{typeLabel(row)}</td>
                                        <td className="py-1.5 text-right tabular-nums">{formatNumber(row.readers)}</td>
                                        <td className="py-1.5 text-right tabular-nums">{fmtPct(row.reads_pct)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                    <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Globe size={14} /> Tipos de Atlas
                    </p>
                    {atlasTypes.length === 0 ? (
                        <p className="text-xs text-muted-foreground">Las fichas de Atlas se miden al actualizar app y web</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[11px] text-muted-foreground text-left">
                                    <th className="pb-2 font-medium">Tipo de agua</th>
                                    <th className="pb-2 font-medium text-right">Lectores</th>
                                    <th className="pb-2 font-medium text-right">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {atlasTypes.map((row) => (
                                    <tr key={row.key} className="border-t border-border/60">
                                        <td className="py-1.5">{typeLabel(row)}</td>
                                        <td className="py-1.5 text-right tabular-nums">{formatNumber(row.readers)}</td>
                                        <td className="py-1.5 text-right tabular-nums">{fmtPct(row.reads_pct)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {data.top_articles.length > 0 ? (
                <div className="rounded-xl border border-border bg-card p-4">
                    <p className="text-sm font-semibold text-foreground mb-3">Piezas más leídas</p>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[11px] text-muted-foreground text-left">
                                <th className="pb-2 font-medium">Artículo</th>
                                <th className="pb-2 font-medium">Origen</th>
                                <th className="pb-2 font-medium text-right">Lecturas</th>
                                <th className="pb-2 font-medium text-right">Tiempo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.top_articles.map((row) => (
                                <tr key={`${row.surface}-${row.slug}`} className="border-t border-border/60">
                                    <td className="py-1.5">
                                        <p className="text-foreground">{row.title}</p>
                                        <p className="text-[11px] text-muted-foreground">{row.type_label}</p>
                                    </td>
                                    <td className="py-1.5 text-muted-foreground">{SURFACE_LABELS[row.surface] ?? row.surface}</td>
                                    <td className="py-1.5 text-right tabular-nums">{formatNumber(row.reads)}</td>
                                    <td className="py-1.5 text-right tabular-nums">{formatDurationSec(row.dwell_p50_sec)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}

            <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
                <Info size={12} className="mt-0.5 shrink-0" />
                El tiempo y los saltos en web/app in-app empiezan con esta versión. El histórico de blog usa lecturas de Academia (sin duración).
            </p>
        </section>
    );
}
